// /src/store/actions/settingsActions.js

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  ERROR_FETCH_API_KEYS,
  ERROR_NEW_IP_NOT_PROVIDED,
  ERROR_IP_ALREADY_ADDED,
  ERROR_FAILED_TO_ADD_IP,
  ERROR_FAILED_TO_REMOVE_IP,
  ERROR_FAILED_TO_ADD_SENDER,
  ERROR_FAILED_TO_SET_SENDER,
  ERROR_FAILED_TO_SET_CALLBACK,
  ERROR_FAILED_TO_ADD_API_KEY
} from "../../utils/constants";
import { stringify } from "qs-esm";

import {
  addIp as addIpAction,
  removeIp as removeIpAction,
  addSenderNumber as addSenderNumberAction,
  setSelectedSenderNumber as setSelectedSenderNumberAction,
  setCallbackUrl as setCallbackUrlAction,
  setApiKeys,
  addApiKey as addApiKeyAction,
  removeApiKey as removeApiKeyAction,
} from "../reducers/settingsReducer";
import { transformApiKeysDataFromOffice } from "../../transformers/office/ingressTransformer";
import { getClientSecrets } from "./authActions";
import { transformClientDataToOffice } from "../../transformers/office/egressTransformer";
import { api } from "../../utils/api";

// todo: finalize fetch
export const fetchApiKeys = createAsyncThunk(
  "settings/fetchApiKeys",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { clientId } = getClientSecrets();
      const stringifiedQuery = stringify({
        and: [
          {
            client: {
              equals: clientId,
            },
            archived: {
              equals: false,
            },
          },
        ],
      });

      const response = await api.get(`/apiKeys?${stringifiedQuery}`);
      const apiKeys = transformApiKeysDataFromOffice(response.data.docs);

      dispatch(setApiKeys(apiKeys));
    } catch (error) {
      return rejectWithValue(ERROR_FETCH_API_KEYS);
    }
  }
);

// Add IP to whitelist
export const addIp = createAsyncThunk(
  "settings/addIp",
  async ({ newIp, existingIps }, { dispatch, rejectWithValue }) => {
    try {
      if (!newIp) return rejectWithValue(ERROR_NEW_IP_NOT_PROVIDED);
      let updatedIps = [];
      if (existingIps) updatedIps = [...existingIps];
      if (updatedIps.includes(newIp)) return rejectWithValue(ERROR_IP_ALREADY_ADDED);

      updatedIps.push(newIp);

      const { clientId } = getClientSecrets();

      const dataToSend = transformClientDataToOffice({
        ipList: updatedIps,
      });

      await api.patch(`/clients/${clientId}`, {
        ipList: dataToSend.ipList,
      });

      dispatch(addIpAction(newIp));
    } catch (error) {
      return rejectWithValue(ERROR_FAILED_TO_ADD_IP);
    }
  }
);

// Remove IP from whitelist
export const removeIp = createAsyncThunk(
  "settings/removeIp",
  async ({ removeMe, existingIps }, { dispatch, rejectWithValue }) => {
    try {
      let newIpsList;
      if (removeMe && existingIps && existingIps.length > 0) {
        newIpsList = existingIps.filter((item) => item !== removeMe);
      }

      const { clientId } = getClientSecrets();

      const dataToSend = transformClientDataToOffice({
        ipList: newIpsList,
      });

      await api.patch(`/clients/${clientId}`, {
        ipList: dataToSend.ipList,
      });

      dispatch(removeIpAction(removeMe));
    } catch (error) {
      return rejectWithValue("FTR");
    }
  }
);

// Add sender number
export const addSenderNumber = createAsyncThunk(
  "settings/addSenderNumber",
  async ({ senderNumbers, newSenderNumber }, { dispatch, rejectWithValue }) => {
    try {
      let updatedSenderNumbers = [];
      if (senderNumbers) updatedSenderNumbers = [...senderNumbers];
      if (newSenderNumber && !updatedSenderNumbers.includes(newSenderNumber))
        updatedSenderNumbers.push(newSenderNumber);
      const { clientId } = getClientSecrets();

      const dataToSend = transformClientDataToOffice({
        senderNumbers: updatedSenderNumbers,
      });

      await api.patch(`/clients/${clientId}`, {
        senderNumbers: dataToSend.senderNumbers,
      });

      dispatch(addSenderNumberAction(newSenderNumber));
    } catch (error) {
      return rejectWithValue(ERROR_FAILED_TO_ADD_SENDER);
    }
  }
);

export const setSelectedSenderNumber = createAsyncThunk(
  "settings/setSelectedSenderNumber",
  async (selectedSenderNumber, { dispatch, rejectWithValue }) => {
    try {
      const { clientId } = getClientSecrets();

      const dataToSend = transformClientDataToOffice({
        selectedSenderNumber: selectedSenderNumber,
      });

      const res = await api.patch(`/clients/${clientId}`, {
        selectedSenderNumber: dataToSend.selectedSenderNumber,
      });

      dispatch(setSelectedSenderNumberAction(selectedSenderNumber));
    } catch (error) {
      return rejectWithValue(ERROR_FAILED_TO_SET_SENDER);
    }
  }
);

// Update callback URL
export const setCallbackUrl = createAsyncThunk(
  "settings/setCallbackUrl",
  async (callbackUrl, { dispatch, getState, rejectWithValue }) => {
    try {
      const { clientId } = getClientSecrets();

      const dataToSend = transformClientDataToOffice({
        callbackUrl: callbackUrl,
      });

      const res = await api.patch(`/clients/${clientId}`, {
        callbackUrl: callbackUrl,
      });

      dispatch(setCallbackUrlAction(callbackUrl));
    } catch (error) {
      return rejectWithValue(ERROR_FAILED_TO_SET_CALLBACK);
    }
  }
);

export const addApiKey = createAsyncThunk(
  "settings/addApiKey",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post("/apiKeys", { key: "jimmy" });

      const key = response.data.doc.key;
      const id = response.data.doc.id;

      // Dispatch action to add the new API key to the state
      dispatch(addApiKeyAction({ id, key }));
    } catch (error) {
      return rejectWithValue(ERROR_FAILED_TO_ADD_API_KEY);
    }
  }
);

// Remove API key
export const removeApiKey = createAsyncThunk(
  "settings/archiveApiKey",
  async (apiKeyId, { dispatch, rejectWithValue }) => {
    try {
      await api.patch(`/apiKeys/${apiKeyId}`, {
        archived: true,
      });

      // Dispatch action to update the API key in the state
      dispatch(removeApiKeyAction(apiKeyId));
    } catch (error) {
      return rejectWithValue("FTR");
    }
  }
);
